import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import { JoyAi } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useLocation, useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHasSeenOnboardingVenueDialog } from '@shared/utils/accommodations';
import { useTour } from '@shared/components/Tour';
import { RoomBlockRatesDialog } from './RoomBlockRatesDialog';
import { useUserConsent } from '@shared/utils/userConsent';
import { useSuppressGetStartedCoachmark } from '@shared/components/GetStartedOnboarding';
import { useWebsiteEditorDialogContext, useRegisterDialogIds } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_DIALOGS } from '../../Accommodations.dialogs';
import type { DialogReadiness } from '@shared/hooks/useDialogManager';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useEventVenueDialog } from '../EventVenueDialog/useEventVenueDialog';
import { useWebsiteEditorContext } from '@apps/admin/routes/WebsiteEditor/context/WebsiteEditorContext/WebsiteEditor.context';
import { AutoPopulatedMoreHotelsTooltip } from './AutoPopulatedMoreHotelsTooltip';
import { useAutoPopulatedAccommodations } from '../../hooks/useAutoPopulatedAccommodations';
import { useAutoPopulationWithDataRefetch } from '../../hooks/useAutoPopulationWithDataRefetch';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * Button component that opens the room block rates dialog.
 * Only renders if there are newly populated accommodations and the feature flag is enabled.
 * Displays an AI icon button that opens the dialog when clicked.
 */
type StartTourButtonProps = Readonly<{
  showTour: boolean;
  /** Set to true when Suggest CTA auto-population completes — forces tooltip instead of dialog */
  suggestRePopulationComplete?: boolean;
  /** Called when the suggest re-population tooltip is dismissed */
  onSuggestTooltipDismissed?: () => void;
  /** When true, hides the sparkle icon button but still renders dialogs/tooltips. Used by the More menu variant. */
  hideButton?: boolean;
  /** Ref populated with the button click handler so a parent (e.g. More menu) can trigger the tour programmatically. */
  triggerRef?: React.MutableRefObject<(() => void) | null>;
}>;

const DIALOG_IDS: readonly string[] = [ACCOMMODATIONS_DIALOGS.ACCOMMODATION_TOUR_TOOLTIP];

export const StartTourButton = ({ showTour, suggestRePopulationComplete, onSuggestTooltipDismissed, hideButton, triggerRef }: StartTourButtonProps) => {
  useRegisterDialogIds(DIALOG_IDS);
  const { tq } = useTranslation('travelMap');

  const { registerDialog, dismiss, isOpen: isDialogOpen, requestShow } = useWebsiteEditorDialogContext(ACCOMMODATIONS_DIALOGS.ACCOMMODATION_TOUR_TOOLTIP);
  const tourDialogIsOpen = isDialogOpen();

  // Persist the tour dialog open state until the tour is completed, so that dialog manager doesn't consider it as closed when the dialog is dismissed.
  const [isRoomBlockDialogOpen, setIsRoomBlockDialogOpen] = useState(tourDialogIsOpen);

  const tour = useTour();
  const location = useLocation();
  const { venueInfo } = useEventPlaces();
  // Use endsWith to avoid false positives if event handle contains 'stay-areas'
  const isStayAreasPage = location.pathname.endsWith('/stay-areas');

  const { loadingData, autoPopulatedAccommodations, isFirstTimeAutoPopulation } = useAutoPopulatedAccommodations();
  const newlyPopulatedCount = autoPopulatedAccommodations.length;

  const { requestOpen, isDashboardRedirect } = useEventVenueDialog();
  const { setHasSeenOnboardingVenueDialog } = useHasSeenOnboardingVenueDialog();

  const { isVisiblePreview } = useWebsiteEditorContext();

  const { status } = useAutoPopulationWithDataRefetch({
    mode: 'poll-only',
    context: 'StartTourButton',
  });

  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const telemetry = useAdminAccommodationsTelemetry();
  const { pageSlug } = useAccommodationsContext();

  // markTourAsSeen fires the consent mutation without awaiting it, so getUserConsent stays stale
  // (false) until the mutation + refetch round-trips. Without this, closing the dialog re-triggers
  // shouldRequestShow on the very next render (still reading the stale value) and reopens it,
  // requiring a second close click. This ref closes that window immediately, optimistically.
  const hasSeenTourOptimisticRef = useRef(false);
  const { eventHandle } = useEventInfo();

  // Nothing in the route tree remounts this component when the edited event changes, so without
  // this reset a "seen" flag set while dismissing the dialog for one event would incorrectly
  // suppress it for a different event edited later in the same mounted lifetime.
  useEffect(() => {
    hasSeenTourOptimisticRef.current = false;
  }, [eventHandle]);

  // Clear the optimistic flag once the real consent value confirms it — otherwise it would
  // permanently mask a later legitimate reset to false (EventVenueDialog resets
  // hasSeenAccommodationsTour after a venue change so the tour runs again).
  useEffect(() => {
    if (hasSeenTourOptimisticRef.current && getUserConsent('hasSeenAccommodationsTour')) {
      hasSeenTourOptimisticRef.current = false;
    }
  }, [getUserConsent]);

  // Determine if the tour dialog should be shown
  const dialogReadiness = useMemo((): DialogReadiness => {
    if (loadingUserConsent || loadingData || status === 'PENDING') return 'loading';

    const venueDialogTakesPriority = isDashboardRedirect;
    if (venueDialogTakesPriority || isStayAreasPage) return 'skip';

    const hasSeenAccommodationsTour = hasSeenTourOptimisticRef.current || getUserConsent('hasSeenAccommodationsTour');

    // Check all conditions:
    // 1. Auto population is completed (and not loading)
    // 2. User hasn't seen the accommodations tour yet OR Suggest CTA just completed
    if (status === 'COMPLETED' && (!hasSeenAccommodationsTour || suggestRePopulationComplete)) {
      return 'ready';
    }

    return 'skip';
  }, [loadingUserConsent, loadingData, getUserConsent, status, isDashboardRedirect, isStayAreasPage, suggestRePopulationComplete]);

  const prevDialogReadinessRef = useRef<DialogReadiness | null>(null);

  useEffect(() => {
    registerDialog(dialogReadiness);

    if (prevDialogReadinessRef.current === 'skip' && dialogReadiness === 'ready' && !isStayAreasPage) {
      requestShow();
    }

    prevDialogReadinessRef.current = dialogReadiness;
  }, [registerDialog, dialogReadiness, requestShow, isStayAreasPage]);

  // Dialog manager rejects after dismissal, but wants to re-request when status newly hits COMPLETED
  const shouldRequestShow = useMemo(() => {
    return (
      // All conditions must be met for the dialog to be shown
      dialogReadiness === 'ready' &&
      // Tour dialog is not currently open
      !tourDialogIsOpen &&
      // At least one accommodation was auto-populated
      newlyPopulatedCount > 0 &&
      // User hasn't seen the tour yet OR Suggest CTA just completed
      (!(hasSeenTourOptimisticRef.current || getUserConsent('hasSeenAccommodationsTour')) || suggestRePopulationComplete) &&
      // On mobile we toggle the preview  vs edit mode, so we don't want to show the tour dialog in preview mode.
      !isVisiblePreview &&
      // Show the tour button if the showTour flag is true
      showTour &&
      // Suppress when on stay-areas page to avoid conflicting with stay areas tooltip
      !isStayAreasPage
    );
  }, [dialogReadiness, tourDialogIsOpen, newlyPopulatedCount, getUserConsent, isVisiblePreview, showTour, isStayAreasPage, suggestRePopulationComplete]);

  useEffect(() => {
    if (shouldRequestShow) {
      requestShow();
    }
  }, [shouldRequestShow, requestShow]);

  // The "Get started" tour's step-4 coachmark points at this same page, so the two onboardings
  // take turns: this modal goes first for anyone who hasn't seen it, and the coachmark waits
  // until it is completed or dismissed. Never both at once.
  //
  // Keyed on whether this modal will actually appear — NOT merely on "hasn't seen it". Plenty of
  // users never get this modal at all (nothing auto-populated, stay-areas page, venue dialog
  // taking priority, preview mode), and gating on the consent alone would suppress their
  // coachmark forever. `shouldRequestShow` is the same predicate that decides to open it, so the
  // hold tracks it exactly; `loading` covers the brief window before we know, so the coachmark
  // never flashes ahead of a modal that is about to take the screen.
  const isAccommodationsOnboardingPending = dialogReadiness === 'loading' || shouldRequestShow || tourDialogIsOpen || isRoomBlockDialogOpen;
  useSuppressGetStartedCoachmark('accommodationsTour', isAccommodationsOnboardingPending);

  // Open the dialog when the button is clicked — explicit user action, must always win.
  const handleButtonClick = useEventCallback(() => {
    telemetry.hotelsSparkleClicked(pageSlug, {
      newlyPopulatedCount,
      hasSeenTour: hasSeenTourOptimisticRef.current || Boolean(getUserConsent('hasSeenAccommodationsTour')),
    });
    requestShow({ force: true });
  });

  // Expose the click handler to parent via ref (used by MoreMenu variant)
  useEffect(() => {
    if (triggerRef) {
      triggerRef.current = handleButtonClick;
    }
    return () => {
      if (triggerRef) {
        triggerRef.current = null;
      }
    };
  }, [triggerRef, handleButtonClick]);

  const markTourAsSeen = () => {
    if (!(hasSeenTourOptimisticRef.current || getUserConsent('hasSeenAccommodationsTour'))) {
      hasSeenTourOptimisticRef.current = true;
      setUserConsent('hasSeenAccommodationsTour', true);
    }
  };

  const handleShowHotelPicks = useEventCallback(() => {
    setIsRoomBlockDialogOpen(false);
    onSuggestTooltipDismissed?.();
    requestAnimationFrame(() => {
      // If have auto populated hotels, run the tour and mark tour as seen
      if (newlyPopulatedCount > 0) {
        tour.state.stateProps.run();
        // Once the tour is run, we can mark it as seen.
        markTourAsSeen();
      } else if (!venueInfo.hasVenue) {
        // If no venue, dismiss the tour dialog, show add venue dialog, mark venue dialog as seen
        dismiss();
        requestOpen();
        setHasSeenOnboardingVenueDialog();
        // Once the venue dialog is shown, we will auto populate the hotels. and tour will be run again, from venue dialog.
        // We need to mark tour as seen, so that the tour dialog won't be shown again.
        markTourAsSeen();
      } else {
        // If have venue but no auto populated hotels, just dismiss and let the user explore the page on their own
        dismiss();
        markTourAsSeen();
      }
    });
  });

  const handleDialogClose = useEventCallback(() => {
    dismiss();
    setIsRoomBlockDialogOpen(false);
    markTourAsSeen();
    onSuggestTooltipDismissed?.();
  });

  // Open the dialog when the tour dialog is opened
  useEffect(() => {
    if (tourDialogIsOpen) {
      setIsRoomBlockDialogOpen(true);
    }
  }, [tourDialogIsOpen]);

  // Dismiss dialog when tour closes (handles both completion and manual close)
  const prevTourOpenRef = useRef(tour.state.stateProps.isOpen);
  useEffect(() => {
    // If tour was open and is now closed, dismiss the dialog
    if (prevTourOpenRef.current && !tour.state.stateProps.isOpen) {
      dismiss();
    }
    prevTourOpenRef.current = tour.state.stateProps.isOpen;
  }, [tour.state.stateProps.isOpen, dismiss]);

  if (status === 'PENDING') {
    return null;
  }

  const hasSeenTour = hasSeenTourOptimisticRef.current || Boolean(getUserConsent('hasSeenAccommodationsTour'));

  // Tooltip: re-population (venue change or Suggest CTA)
  const shouldShowAutoPopulatedTooltip = isRoomBlockDialogOpen && (suggestRePopulationComplete || (!isFirstTimeAutoPopulation && !hasSeenTour));

  // Dialog: first auto-population or user has already seen the tour (but not Suggest re-population)
  const shouldShowRoomBlockDialog = isRoomBlockDialogOpen && !suggestRePopulationComplete && (isFirstTimeAutoPopulation || hasSeenTour);

  return (
    <>
      {/* Show the button only if there are newly populated hotels and the venue is present */}
      {!hideButton && venueInfo.hasVenue && newlyPopulatedCount > 0 && (
        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={handleButtonClick}
          aria-label={tq`Start accommodations tour`}
          paddingX={0}
          minWidth={pxToRem(32)}
          width={pxToRem(32)}
          height={pxToRem(32)}
        >
          <JoyAi
            size={32}
            color="newUIBrandPrimary"
          />
        </ButtonV2>
      )}
      {/* Suspense catches translation namespace loads (travelMap, tour) to prevent flash */}
      <Suspense fallback={null}>
        {/* Tour Dialog should be visible on first time, even if don't have venue and auto populated hotels */}
        <RoomBlockRatesDialog
          isOpen={shouldShowRoomBlockDialog}
          onCloseDialog={handleDialogClose}
          onShowHotelPicks={handleShowHotelPicks}
          hotelCount={newlyPopulatedCount}
          hasVenue={venueInfo.hasVenue}
        />
      </Suspense>
      {/* No Suspense here — tooltip relies on fixed-position anchor + requestAnimationFrame timing.
         Suspense would unmount/remount and break Popper positioning.
         Its translation namespace (travelMap) ships with this chunk — see withNamespacePreload. */}
      <AutoPopulatedMoreHotelsTooltip
        isOpen={shouldShowAutoPopulatedTooltip}
        onCloseDialog={handleDialogClose}
        onShowHotelPicks={handleShowHotelPicks}
      />
    </>
  );
};
